import { useEffect } from "react";
import { runStore } from "../store/runStore.js";
import { runProgress } from "../lib/forecast/reducer.js";
import { STATUS } from "../lib/forecast/model.js";
import { laneTier } from "../lib/forecast/engines.js";
import {
  ladderForTag,
  formatProgressStep,
  isPaceableQuickStep,
  pacedQuickStepIndex,
  QUICK_WEIGHING_DWELL_MS,
} from "../lib/forecast/progressSteps.js";

/** Whether `run` is a live quick-tier run sitting on Researching. */
function paceable(run) {
  return (
    !!run &&
    run.status === STATUS.RUNNING &&
    isPaceableQuickStep(laneTier(run.backend), run.progress?.stepIdx)
  );
}

/**
 * Watch the run store and pace live quick-tier runs from Researching to Weighing
 * evidence after a dwell (see progressSteps' header: the server never signals that
 * step). One timer per eligible run, armed when it enters Researching and cleared as
 * soon as it stops being eligible — a real later step, cancel, completion, error, or
 * removal. Returns `stop()`, which clears every timer and unsubscribes.
 *
 * Owner rows only: shared rows don't stream progress into this tab.
 *
 * @param {{ getState: Function, subscribe: Function, dispatch: Function }} store
 * @param {{ dwellMs?: number, now?: () => number, setTimeoutFn?: Function, clearTimeoutFn?: Function }} [opts]
 * @returns {{ stop: () => void }}
 */
export function startQuickLadderPacer(store, opts = {}) {
  const dwellMs = opts.dwellMs ?? QUICK_WEIGHING_DWELL_MS;
  const now = opts.now || Date.now;
  const setT = opts.setTimeoutFn || setTimeout;
  const clearT = opts.clearTimeoutFn || clearTimeout;
  const armed = new Map(); // runId → { timer, since }
  let lastRuns = null;
  let stopped = false;

  const fire = (runId) => {
    const entry = armed.get(runId);
    armed.delete(runId);
    if (stopped || !entry) return;
    const run = store.getState().runsById[runId];
    if (!paceable(run)) return;
    const tag = laneTier(run.backend);
    const elapsed = now() - entry.since;
    const idx = pacedQuickStepIndex(tag, run.progress.stepIdx, elapsed, dwellMs);
    if (idx < 0) {
      // Timer fired early against the clock: wait out the remainder.
      const rest = Math.max(1, dwellMs - elapsed);
      armed.set(runId, { timer: setT(() => fire(runId), rest), since: entry.since });
      return;
    }
    store.dispatch(
      runProgress(runId, {
        stage: run.progress.stage ?? null,
        step: null,
        stepIdx: idx,
        pct: run.progress.pct ?? null,
        message: formatProgressStep(ladderForTag(tag), idx, null),
      }),
    );
  };

  const sync = () => {
    if (stopped) return;
    const runs = store.getState().runsById;
    if (runs === lastRuns) return;
    lastRuns = runs;
    for (const [runId, entry] of armed) {
      if (!paceable(runs[runId])) {
        clearT(entry.timer);
        armed.delete(runId);
      }
    }
    for (const [runId, run] of Object.entries(runs)) {
      if (armed.has(runId) || !paceable(run)) continue;
      armed.set(runId, { timer: setT(() => fire(runId), dwellMs), since: now() });
    }
  };

  const unsubscribe = store.subscribe(sync);
  sync();
  return {
    stop: () => {
      stopped = true;
      unsubscribe();
      for (const entry of armed.values()) clearT(entry.timer);
      armed.clear();
    },
  };
}

/** Run the quick-ladder pacer for the lifetime of the app shell. */
export function useQuickLadderPacer() {
  useEffect(() => {
    const pacer = startQuickLadderPacer(runStore);
    return pacer.stop;
  }, []);
}
